/**
 * The auth context object and its type.
 *
 * Kept in its own module (no components) so that `AuthContext.tsx` exports only
 * the provider component and `hooks/useAuth.ts` only the hook. React Fast
 * Refresh can only replace a module whose exports are all components, so mixing
 * a component and a hook in one file silently degrades hot reloading for the
 * whole tree under the provider.
 */

import { type AccountInfo } from '@azure/msal-browser'
import { createContext } from 'react'

export interface AuthContextType {
    isAuthenticated: boolean
    user: AccountInfo | null
    login: () => void
    logout: () => void
    /** Sign in again in a popup, keeping the page and its jobs; false when that did not work. */
    reauthenticate: () => Promise<boolean>
    /** The bearer token; null when signed out or the sign-in has expired. Rejects when it could not be fetched. */
    getToken: () => Promise<string | null>
    loading: boolean
    authEnabled: boolean
    /** Inside the Media Manager: its sign-in, so the app offers no sign-in or sign-out of its own. */
    hostSignIn: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)
